import { istTeam, type PersonRole } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { anfragenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenVerweis } from './AufgabeKontext'
import { MarkdownFeld } from './MarkdownFeld'
import { Button, Card, CardPad, SectionTitle } from './ui/primitives'

/**
 * **Anfragen aus den Fachbereichen** (Phase 9, Konzept Kap. 4.8).
 *
 * > *„Nicht alles, was ein Abteilungsleiter einreicht, ist eine Aufgabe – oft ist es eine
 * > Frage («Wann kriege ich mein POP-Spaltenfeld in Buchungen?»)."*
 *
 * ## Warum das Formular und die Liste in einer Datei stehen
 *
 * Weil sie **dieselbe Sache aus zwei Richtungen** sind und in beiden Ansichten gebraucht
 * werden: der Fachbereich fragt in „Mein Bereich" und sieht dort seine Antworten, das Team
 * entscheidet im Eingang. Zwei Dateien wären zwei Stellen, an denen dieselbe Zeile anders
 * aussieht – genau der Fehler, aus dem am 03.08.2026 sechs Kopien des Aufgabendetails
 * geworden waren ([E-56](../../../../docs/ENTSCHEIDUNGEN.md)).
 */

export function FrageStellen() {
  const queryClient = useQueryClient()
  const [frage, setFrage] = useState('')
  const [bereich, setBereich] = useState('')
  const [gestellt, setGestellt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const stellen = useMutation({
    mutationFn: () =>
      trpc.anfragen.stellen.mutate({
        frage: frage.trim(),
        ...(bereich.trim() ? { bereich: bereich.trim() } : {}),
      }),
    onSuccess: () => {
      setFrage('')
      setBereich('')
      setGestellt(true)
      setFehler(null)
      void queryClient.invalidateQueries({ queryKey: ['anfragen'] })
    },
    onError: (e: Error) => {
      setGestellt(false)
      setFehler(e.message)
    },
  })

  return (
    <>
      <SectionTitle hint="Eine Frage ist keine Aufgabe – sie wird beantwortet, nicht eingeplant">
        Etwas fragen
      </SectionTitle>
      <Card className="mb-4">
        <CardPad>
          {gestellt && (
            <p className="mb-2.5 rounded-lg bg-[#eef7ee] px-2.5 py-2 text-[12.5px] text-[#1d6b34]">
              <strong>Angekommen.</strong> Work sieht sofort im Bestand nach – gleich unten steht,
              was es gefunden hat. <em>Bestätigt</em> ist das erst, wenn jemand aus der Entwicklung
              draufgesehen hat.
            </p>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (frage.trim().length >= 10) stellen.mutate()
            }}
          >
            <MarkdownFeld
              wert={frage}
              setzen={setFrage}
              zeilen={3}
              platzhalter="Wann kriege ich mein POP-Spaltenfeld in Buchungen?"
              beiAbsenden={() => {
                if (frage.trim().length >= 10) stellen.mutate()
              }}
            />
            <label className="mt-2 block text-[11.5px] font-semibold text-ink2">
              Bereich (freiwillig)
              <input
                value={bereich}
                onChange={(e) => setBereich(e.target.value)}
                placeholder="z. B. Buchungen, Tiefbau, Abrechnung"
                className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
              />
            </label>

            {fehler && (
              <p className="mt-2 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
                {fehler}
              </p>
            )}

            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={frage.trim().length < 10 || stellen.isPending}
              >
                {stellen.isPending ? 'Sucht im Bestand …' : 'Fragen'}
              </Button>
              <span className="text-[11px] text-muted">
                Markdown · ⌘↵ sendet · Antwort meist in Sekunden
              </span>
            </div>
          </form>
        </CardPad>
      </Card>
    </>
  )
}

/**
 * **Die Liste** – für den Fachbereich seine eigenen Fragen, für das Team alle offenen.
 *
 * Der Unterschied steckt nicht im Filter, sondern in den Knöpfen: wer fragt, liest; wer
 * entscheidet, antwortet. Die Sichtbarkeit selbst kommt vom Server.
 */
export function AnfragenListe({ role }: { role: PersonRole }) {
  const { data: anfragen = [] } = useQuery(anfragenQuery)
  const darfEntscheiden = istTeam(role)
  /**
   * **Beantwortetes ist Archiv, kein Eingang** (Nils, 20.08.2026: *„die verbrauchen unnötig
   * Platz"*).
   *
   * Eine erledigte Anfrage ist eine Auskunft, die schon gegeben wurde – sie steht hier nur
   * noch zum Nachschlagen, kostet aber denselben Platz wie eine, über die jemand entscheiden
   * muss. Zugeklappt bleibt sie erreichbar; aufgeklappt wäre sie im Weg. Der Knopf nennt die
   * Zahl, damit „zugeklappt" nicht mit „gibt es nicht" verwechselt wird.
   */
  const [alteZeigen, setAlteZeigen] = useState(false)

  const offene = anfragen.filter((a) => a.status === 'offen' || a.status === 'vorgeschlagen')
  const erledigte = anfragen.filter((a) => a.status !== 'offen' && a.status !== 'vorgeschlagen')

  if (anfragen.length === 0) return null

  /**
   * Gibt es nichts Offenes und ist das Archiv zu, bleibt von der ganzen Sektion eine Zeile –
   * **die Überschrift entfällt mit**. „Anfragen (0 offen)" über einem leeren Bereich ist die
   * Art Kopfzeile, die man wegscrollt, ohne sie zu lesen.
   */
  const nurArchiv = offene.length === 0 && !alteZeigen

  return (
    <div className="mb-4">
      {!nurArchiv && (
        <SectionTitle
          hint={
            darfEntscheiden
              ? 'Vorschlag der KI mit Beleg – bestätigen, umschreiben oder zur Aufgabe machen'
              : 'Was Work im Bestand gefunden hat – bestätigt wird es von der Entwicklung'
          }
        >
          {darfEntscheiden ? `Anfragen (${offene.length} offen)` : 'Meine Fragen'}
        </SectionTitle>
      )}

      {(offene.length > 0 || alteZeigen) && (
        <div className="grid gap-2">
          {(alteZeigen ? [...offene, ...erledigte] : offene).slice(0, 40).map((a) => (
            <AnfrageKarte key={a.id} anfrage={a} darfEntscheiden={darfEntscheiden} />
          ))}
        </div>
      )}

      {erledigte.length > 0 && (
        <button
          type="button"
          onClick={() => setAlteZeigen((z) => !z)}
          aria-expanded={alteZeigen}
          className={`cursor-pointer text-[11.5px] text-muted hover:text-ink2 hover:underline ${
            offene.length > 0 || alteZeigen ? 'mt-1.5' : ''
          }`}
        >
          {alteZeigen
            ? 'beantwortete Anfragen ausblenden'
            : `${erledigte.length} beantwortete ${erledigte.length === 1 ? 'Anfrage' : 'Anfragen'} anzeigen`}
        </button>
      )}
    </div>
  )
}

type Anfrage = NonNullable<Awaited<ReturnType<typeof trpc.anfragen.liste.query>>>[number]

function AnfrageKarte({
  anfrage,
  darfEntscheiden,
}: {
  anfrage: Anfrage
  darfEntscheiden: boolean
}) {
  const queryClient = useQueryClient()
  const [eigene, setEigene] = useState<string | null>(null)
  const [titel, setTitel] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['anfragen'] })
  const beiFehler = (e: Error) => setFehler(e.message)

  const beantworten = useMutation({
    mutationFn: (antwort?: string) =>
      trpc.anfragen.beantworten.mutate({ id: anfrage.id, ...(antwort ? { antwort } : {}) }),
    onSuccess: () => {
      setEigene(null)
      neuLaden()
    },
    onError: beiFehler,
  })
  const alsAufgabe = useMutation({
    mutationFn: () => trpc.anfragen.alsAufgabe.mutate({ id: anfrage.id, titel: titel.trim() }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })
  const verwerfen = useMutation({
    mutationFn: () => trpc.anfragen.verwerfen.mutate({ id: anfrage.id }),
    onSuccess: neuLaden,
    onError: beiFehler,
  })

  const offen = anfrage.status === 'offen' || anfrage.status === 'vorgeschlagen'

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2 text-[12.5px]">
          <strong className="min-w-0 break-words">{anfrage.frage}</strong>
          <span className="ml-auto shrink-0 text-[11px] text-muted">
            {anfrage.stellerName} ·{' '}
            {new Date(anfrage.createdAt).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
            })}
            {anfrage.bereich ? ` · ${anfrage.bereich}` : ''}
          </span>
        </div>

        {anfrage.status === 'offen' && (
          <p className="mt-1.5 text-[12px] text-muted">Wird gerade im Bestand nachgesehen …</p>
        )}

        {anfrage.vorschlagText && (
          <div className="mt-1.5 rounded-lg bg-[#f7f6f4] px-2.5 py-2 text-[12.5px]">
            <div className="mb-1 flex flex-wrap items-baseline gap-2 text-[10.5px] font-semibold tracking-[0.5px] text-muted uppercase">
              {anfrage.status === 'beantwortet' ? 'Antwort' : 'Vorschlag'}
              {anfrage.konfidenz !== null && (
                <span className="normal-case">Konfidenz {anfrage.konfidenz} %</span>
              )}
            </div>
            <p className="break-words whitespace-pre-wrap text-ink2">
              {anfrage.antwort ?? anfrage.vorschlagText}
            </p>

            {/**
             * **Der Beleg ist der Punkt.** Kap. 4.8 verlangt ihn ausdrücklich; ohne ihn ist
             * „ist eingeplant" für den Fragenden nicht von einer erfundenen Auskunft zu
             * unterscheiden. Jede Kennung hier wurde gegen die Kandidatenliste geprüft –
             * was das Modell erfunden hat, steht gar nicht erst drin.
             */}
            {anfrage.belegteAufgaben.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 border-t border-line pt-1.5">
                <span className="text-[10.5px] font-semibold tracking-[0.5px] text-muted uppercase">
                  Beleg
                </span>
                {anfrage.belegteAufgaben.map((b) => (
                  <AufgabenVerweis
                    key={b.id}
                    aufgabeId={b.id}
                    className="cursor-pointer rounded-md bg-surface px-2 py-px text-[11.5px] text-accent-deep hover:underline"
                    title={b.titel}
                  >
                    {b.kennung ?? b.titel.slice(0, 40)}
                    <span className="ml-1 text-muted">{b.status}</span>
                  </AufgabenVerweis>
                ))}
              </div>
            )}

            {anfrage.belegteAufgaben.length === 0 && anfrage.status !== 'offen' && (
              <p className="mt-1.5 border-t border-line pt-1.5 text-[11px] text-muted">
                Kein Beleg – dazu gibt es im Bestand nichts. Wenn es gebraucht wird, muss eine
                Aufgabe daraus werden.
              </p>
            )}
          </div>
        )}

        {anfrage.taskId && (
          <p className="mt-1.5 text-[11.5px]">
            <AufgabenVerweis
              aufgabeId={anfrage.taskId}
              className="cursor-pointer text-accent-deep hover:underline"
            >
              Daraus wurde eine Aufgabe →
            </AufgabenVerweis>
          </p>
        )}

        {fehler && (
          <p className="mt-1.5 rounded-lg bg-[#fdeaea] px-2.5 py-1.5 text-[12px] text-[#b02a2a]">
            {fehler}
          </p>
        )}

        {darfEntscheiden && offen && (
          <div className="mt-2">
            {eigene === null ? (
              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant="primary"
                  disabled={!anfrage.vorschlagText || beantworten.isPending}
                  onClick={() => beantworten.mutate(undefined)}
                  title="Den Vorschlag unverändert als Antwort festhalten"
                >
                  Vorschlag bestätigen
                </Button>
                <Button onClick={() => setEigene(anfrage.vorschlagText ?? '')}>
                  Selbst antworten
                </Button>
                <Button onClick={() => setTitel(anfrage.frage.slice(0, 120))}>
                  Aufgabe daraus machen
                </Button>
                <Button disabled={verwerfen.isPending} onClick={() => verwerfen.mutate()}>
                  Verwerfen
                </Button>
              </div>
            ) : (
              <>
                <MarkdownFeld wert={eigene} setzen={setEigene} zeilen={3} />
                <div className="mt-1.5 flex gap-1.5">
                  <Button
                    variant="primary"
                    disabled={eigene.trim().length === 0 || beantworten.isPending}
                    onClick={() => beantworten.mutate(eigene.trim())}
                  >
                    Antworten
                  </Button>
                  <Button onClick={() => setEigene(null)}>Abbrechen</Button>
                </div>
              </>
            )}

            {titel && (
              <div className="mt-2 rounded-lg border border-line2 px-2.5 py-2">
                <label className="block text-[11.5px] font-semibold text-ink2">
                  Titel der Aufgabe
                  <input
                    value={titel}
                    onChange={(e) => setTitel(e.target.value)}
                    className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-1.5 text-[13px] font-normal"
                  />
                </label>
                <p className="mt-1 text-[11px] text-muted">
                  Die Aufgabe entsteht im <strong>Eingang</strong> und trägt die Frage im Wortlaut
                  als Beschreibung – genauer wird die Anforderung heute nicht.
                </p>
                <div className="mt-1.5 flex gap-1.5">
                  <Button
                    variant="primary"
                    disabled={titel.trim().length < 5 || alsAufgabe.isPending}
                    onClick={() => alsAufgabe.mutate()}
                  >
                    Anlegen
                  </Button>
                  <Button onClick={() => setTitel('')}>Abbrechen</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardPad>
    </Card>
  )
}
